import React, { useState, useRef, useEffect, useCallback } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import styled from "styled-components";
import { Box, Flex, Text } from "rebass/styled-components";
import { HiArchive } from "react-icons/hi";

import { AllstarUser, StripePaystationState } from "MVVM/State";
import { useUser } from "MVVM/Hooks/useUser";
import {
  STORAGE_ADDONS,
  STORAGE_ADDON_ELIGIBLE_TIERS,
  TIER_NAMES,
  IStorageAddOn,
} from "MVVM/Utilities/SubscriptionTiers";
import { analytics } from "MVVM/Analytics";

interface StorageAddOnPickerProps {
  children: (props: {
    onClick: () => void;
    isEligible: boolean;
  }) => React.ReactNode;
  analyticsSource: string;
  onBeforeOpen?: () => void;
  onSelect?: () => void;
}

export const StorageAddOnPicker = ({
  children,
  analyticsSource,
  onBeforeOpen,
  onSelect,
}: StorageAddOnPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const { allstarUser } = useUser();
  const { loggedIn } = useRecoilValue(AllstarUser);
  const [paystation, setPaystationOpen] = useRecoilState(StripePaystationState);

  const userGroup = allstarUser?.user?.userGroup?.type;
  const isEligible =
    loggedIn && userGroup && STORAGE_ADDON_ELIGIBLE_TIERS.includes(userGroup);
  const addOns = isEligible ? STORAGE_ADDONS[userGroup] : null;

  const handleToggle = useCallback(() => {
    if (!isEligible) return;
    if (!isOpen) onBeforeOpen?.();
    setIsOpen((prev) => !prev);
  }, [isEligible, isOpen, onBeforeOpen]);

  const handleSelect = useCallback(
    (addOn: IStorageAddOn) => {
      analytics.track(`Storage Add-On Click - ${analyticsSource}`, {
        lookupKey: addOn.lookupKey,
        clips: addOn.clips,
        price: addOn.price,
        userGroup,
      });

      setPaystationOpen({
        ...paystation,
        isModalOpen: true,
        type: "subscribe",
        lookUp: addOn.lookupKey,
      });

      setIsOpen(false);
      onSelect?.();
    },
    [analyticsSource, userGroup, paystation, setPaystationOpen, onSelect],
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  if (!isEligible || !addOns) {
    return <>{children({ onClick: () => {}, isEligible: false })}</>;
  }

  return (
    <PickerContainer ref={pickerRef}>
      {children({ onClick: handleToggle, isEligible: true })}
      {isOpen && (
        <Dropdown>
          <DropdownHeader>
            <HiArchive size={14} />
            Add storage to {TIER_NAMES[userGroup]}
          </DropdownHeader>
          {addOns.map((addOn) => (
            <DropdownOption
              key={addOn.lookupKey}
              onClick={() => handleSelect(addOn)}
            >
              <OptionClips>+{addOn.clips} clips</OptionClips>
              <OptionPrice>{addOn.price}/mo</OptionPrice>
            </DropdownOption>
          ))}
        </Dropdown>
      )}
    </PickerContainer>
  );
};

const PickerContainer = styled(Box)`
  position: relative;
`;

const Dropdown = styled(Box)`
  background: ${({ theme }) => theme.colors.darkNebula || theme.colors.navy};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  min-width: 200px;
  overflow: hidden;
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  z-index: 100;
`;

const DropdownHeader = styled(Flex)`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.navy50};
  color: ${({ theme }) => theme.colors.shade300};
  font-family: Barlow;
  font-size: 12px;
  font-weight: 600;
  gap: 6px;
  letter-spacing: 0.3px;
  padding: 10px 14px;
  white-space: nowrap;
`;

const DropdownOption = styled(Flex)`
  align-items: center;
  cursor: pointer;
  gap: 20px;
  justify-content: space-between;
  padding: 14px 16px;
  transition: background 0.1s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.navy50};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.navy50}20;
  }
`;

const OptionClips = styled(Text)`
  color: ${({ theme }) => theme.colors.envy};
  font-family: Barlow;
  font-size: 14px;
  font-weight: 600;
`;

const OptionPrice = styled(Text)`
  color: ${({ theme }) => theme.colors.shade300};
  font-family: Barlow;
  font-size: 14px;
`;
